import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '@/store/store';

import { PreInvoice, PreInvoiceApi } from '../models'; 
import { PaginatedResponse } from '@/types';
import { API_FLEET_URL } from '@/utils';

export interface PreInvoicesFilter {
  dateFrom?: Date | null;
  dateTo?: Date | null;
  businessId?: string;
  driverId?: string;
  serviceTypeId?: string;
  vehicleTypeId?: string;
}

const preInvoiceService = createApi({
  reducerPath: 'preInvoiceService',
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_FLEET_URL}/pre-invoices`,
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState;
      const token = state.auth?.auth?.accessToken || null;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['PreInvoice', 'PreInvoices'],
  endpoints: (builder) => ({
    getPreInvoices: builder.query<
      PaginatedResponse<PreInvoice>,
      {
        dateFrom: string;
        dateTo: string;
        businessId: string;
        limit: number;
        offset: number;
        driverId?: string;
        serviceTypeId?: string;
        vehicleTypeId?: string;
      }
    >({
      query: (args) => {
        const {
          dateFrom,
          dateTo,
          businessId,
          driverId,
          serviceTypeId,
          vehicleTypeId,
          limit,
          offset,
        } = args;
        return {
          url: '/commerce',
          params: {
            scheduledFrom: dateFrom,
            scheduledTo: dateTo,
            commercialBusinessId: businessId,
            driverId: driverId,
            serviceTypeId: serviceTypeId,
            vehicleTypeId: vehicleTypeId,
            limit,
            offset,
          },
        };
      },
      transformResponse: (response: PaginatedResponse<PreInvoice>) => response,
      providesTags: ['PreInvoice'],
    }),

    getPreInvoicesTotal: builder.query<
      { totalAmount: number },
      {
        dateFrom: string;
        dateTo: string;
        businessId?: string;
        driverId?: string;
        serviceTypeId?: string;
        vehicleTypeId?: string;
      }
    >({
      query: (args) => {
        const {
          dateFrom,
          dateTo,
          businessId,
          driverId,
          serviceTypeId,
          vehicleTypeId,
        } = args;
        return {
          url: '/commerce',
          params: {
            scheduledFrom: dateFrom || undefined,
            scheduledTo: dateTo || undefined,
            commercialBusinessId: businessId || undefined,
            driverId: driverId || undefined,
            serviceTypeId: serviceTypeId || undefined,
            vehicleTypeId: vehicleTypeId || undefined,
            limit: 10000,
            offset: 0,
          },
        };
      },
      transformResponse: (response: PaginatedResponse<PreInvoiceApi>) => ({
        totalAmount: response.items.reduce(
          (acc, item) => acc + (item.totalamount || 0),
          0
        ),
      }),
      providesTags: ['PreInvoices'],
    }),
  }),
});

export default preInvoiceService;

export const { useGetPreInvoicesQuery, useGetPreInvoicesTotalQuery } =
  preInvoiceService;
